import { pick } from "lodash";
import { propertyToTitle } from "PFCore/helpers/templates";
import PropTypes from "prop-types";
import React, { useMemo } from "react";

import AutoSelectWrapper from "./field_wrappers/auto_select_wrapper";
import { CheckboxesWrapper } from "./field_wrappers/checkboxes_wrapper";
import CustomValuesEditFieldWrapper from "./field_wrappers/custom_values_edit_field_wrapper";
import InlineCalendarWrapper from "./field_wrappers/inline_calendar_wrapper";
import InlineRangeCalendarWrapper from "./field_wrappers/inline_range_calendar_wrapper";
import InputFieldSetWrapper from "./field_wrappers/input_field_set_wrapper";
import ToggleWrapper from "./field_wrappers/toggle_wrapper";

const propertyTypeToComponent = {
  "string": InputFieldSetWrapper,
  "text": InputFieldSetWrapper,
  "url": InputFieldSetWrapper,
  "link": InputFieldSetWrapper,
  "date": InlineCalendarWrapper,
  "date-time": InlineCalendarWrapper,
  "date-range": InlineRangeCalendarWrapper,
  "select": AutoSelectWrapper,
  "integer": InputFieldSetWrapper,
  "toggle": ToggleWrapper,
  "checkboxes": CheckboxesWrapper,
  "custom_field": CustomValuesEditFieldWrapper
};

const FieldFactory = ({
  template,
  subtemplate,
  property,
  metadata,
  customFields,
  connectedFields,
  onChange,
  customTypes,
  currentProfile,
  qaIdPrefix,
  name,
  description,
  usedOn,
  errors
}) => {
  const Component = propertyTypeToComponent[property.type] || false;
  const isText = property.type === "text";
  const isCustomField = property.type === "custom_field";
  const additionalProps = useMemo(() => {
    if (isCustomField) {
      const customType = customTypes.find((type) => type.name === property.name);
      if (customType) {
        return { currentProfile, customType, qaIdPrefix, name, description, usedOn };
      } else {
        return {};
      }
    } else if (isText) {
      return { inputType: "textarea", minHeight: 40, controlledValue: true };
    }
  }, [property, currentProfile, customTypes, name, description, usedOn]);

  const style = useMemo(() => ({ width: property?.width || "100%", display: "block" }), [property]);

  const value = useMemo(() => {
    if (isCustomField) {
      const customField = customFields.find((customField) => customField.type.name === property.name);
      return customField ? customField.values : [];
    } else {
      return metadata[property.name];
    }
  }, [customFields, metadata]);

  const handleChange = (value) => onChange(value, property);

  const error = () => {
    const keyBegining = `metadata[${property.name}]`;
    const keys = Object.keys(errors).filter((key) => key.indexOf(keyBegining) === 0);

    if (keys.length === 0) {
      return;
    }
    if (property.properties) {
      // nested e.g. availability
      return pick(errors, ...keys);
    } else {
      return errors[keys[0]];
    }
  };

  const label = propertyToTitle(template, property.name, customTypes) || "";

  const requiredInTemplate = template.schema.required.includes(property.name);
  const requiredInSubtemplate = (subtemplate?.schema.required || []).includes(property.name);
  const required = subtemplate ? requiredInSubtemplate : requiredInTemplate;

  const isDescriptiveLabel = label.length > 60;

  return (
    Component && (
      <div style={style}>
        {isDescriptiveLabel && <span>{label}</span>}
        <Component
          property={property}
          connectedFields={connectedFields}
          onChange={handleChange}
          maxLength={property.maxLength}
          error={error()}
          label={isDescriptiveLabel ? "" : label}
          value={value}
          qaId={`${qaIdPrefix}-${property.name}`}
          required={required}
          {...additionalProps}
        />
      </div>
    )
  );
};

FieldFactory.propTypes = {
  template: PropTypes.object.isRequired,
  property: PropTypes.object.isRequired,
  metadata: PropTypes.object.isRequired,
  customFields: PropTypes.array.isRequired,
  connectedFields: PropTypes.array.isRequired,
  customTypes: PropTypes.array.isRequired,
  currentProfile: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  subtemplate: PropTypes.object,
  qaIdPrefix: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  usedOn: PropTypes.string,
  errors: PropTypes.object
};

export default FieldFactory;
